import { Inject, Injectable } from "@angular/core";
import { RxState } from "@rx-angular/state";
import { rxActions } from "@rx-angular/state/actions";
import { catchError, delay, EMPTY, filter, first, map, Observable, switchMap, tap } from "rxjs";

import { LoggerService } from "src/app/logger/logger.service";
import { AssessmentsState } from "../models/assessments-state.interface";
import { AssessmentActions } from "../models/assessment-actions.interface";
import { AssessmentUseCaseEnum } from "../models/assessments.enum";
import { AssessmentTemplateEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentsSplitByUseCaseService } from "../services/assessments-split-by-use-case.service";
import { AssessmentPageResponse, AssessmentResourceId } from "src/app/api/models/assessments/assessment.interface";
import { AssessmentPage } from "../models/assessments.interface";
import { API_CLIENT_ASSESSMENTS_SERVICE } from "src/app/api/injectors/api-client-ai-assessments.token";
import { BaseApiClientAssessmentService } from "src/app/api/base-api-client-assessment.service";
import { AssessmentsTemplate } from "src/app/api/models/assessments/assessment-template.interface"; 
import { AssessmentsMapperService } from "../services/assessments-mapper.service";
import { CustomFieldsV2Service } from "src/app/company-settings/custom-fields-v2/custom-fields-v2.service";

@Injectable({
    providedIn: 'root'
})
export class AiAssessmentsStore extends RxState<AssessmentsState> {

    private readonly loggerName: string = 'AiAssessmentsStore';
    
    private readonly initState = {
        empty: undefined,
        loading: undefined,
        disabled: false,
        templates: undefined,
        assessments: undefined,
    } as AssessmentsState;

    public actions = rxActions<AssessmentActions>();

    constructor(
        private logger: LoggerService,
        private customFieldsV2Service: CustomFieldsV2Service,
        private assessmentsMapperService: AssessmentsMapperService,
        private assessmentsSplitByUseCaseService: AssessmentsSplitByUseCaseService,
        @Inject(API_CLIENT_ASSESSMENTS_SERVICE) private apiClientAiAssessmentsService: BaseApiClientAssessmentService,
    ) {
        super();
        this.set(this.initState);
    }

    initAssessments(): Observable<Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>> {
        return this.customFieldsV2Service.selectLoading().pipe(
            filter(loading => !loading),
            switchMap(() => this.apiClientAiAssessmentsService.getAssessments()),
            first(),
            delay(100),
            map(assessments => this.mapAssessmentsResponse(assessments)),
            tap(assessments => this.set({ assessments })),
            catchError(err => {
                console.error(err);
                this.logger.error(this.loggerName, err);
                return EMPTY;
            })
        );
    }

    /** Refetches assessments from API and updates the store. Used e.g. after mark-as-done/undo in collaboration. */
    refreshAssessments(): Observable<void> {
        return this.apiClientAiAssessmentsService.getAssessments().pipe(
            first(),
            delay(100),
            map(assessments => this.mapAssessmentsResponse(assessments)),
            tap(assessments => this.set({ assessments })),
            map(() => undefined),
            catchError(err => {
                console.error(err);
                this.logger.error(this.loggerName, err);
                return EMPTY;
            })
        );
    }

    private mapAssessmentsResponse(assessments: AssessmentPageResponse[]): Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>> {
        const map = new Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentPage>>();
        assessments.forEach(assessment => {
            const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByLabelsOrType(assessment.type as AssessmentTemplateEnum, assessment.tags);
            if (!map.has(useCase)) {
                map.set(useCase, new Map());
            }
            map.get(useCase)?.set(assessment.id as AssessmentResourceId, this.assessmentsMapperService.mapAssessmentPage(assessment));
        });
        return map;
    }

    initTemplates(): Observable<Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>> {
        return this.apiClientAiAssessmentsService.getTemplates().pipe(
            first(),
            delay(100),
            map(templates => this.mapTemplatesResponse(templates)),
            tap(templates => this.set({ templates })),
            catchError(err => {
                console.error(err);
                this.logger.error(this.loggerName, err);
                return EMPTY;
            })
        );
    }

    private mapTemplatesResponse(templates: AssessmentsTemplate[]): Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>> {
        const map = new Map<AssessmentUseCaseEnum, Map<AssessmentResourceId, AssessmentsTemplate>>();
        templates.forEach(template => {
            const useCase = this.assessmentsSplitByUseCaseService.selectUseCaseByLabelsOrType(template.type, template.tags);
            if (!map.has(useCase)) {
                map.set(useCase, new Map());
            }
            map.get(useCase)?.set(template.id as AssessmentResourceId, template);
        });
        return map;
    }
}